/**
 * Turning what a supervisor does on the live view into input events
 * for the runner's page, and sending them.
 *
 * Mirrors the server contract in
 * `shared/python-core/flow_shared_core/live_protocol.py`. The server
 * validates every event again; this module's job is to never send
 * something it would reject, and to never forward a key that belongs
 * to the supervisor's own browser.
 *
 * Privacy: an event can carry a password or a patient detail. Events
 * are held in memory for a fraction of a second, sent, and dropped.
 * Nothing here logs an event, stores one, or puts one in an error.
 */
import type { LiveInputEvent } from "./liveApi";

export const MAX_TEXT_CHARS = 256;
export const MAX_EVENTS_PER_BATCH = 32;
/** At most ~8 requests a second (the API allows 600 a minute). */
export const FLUSH_INTERVAL_MS = 125;

/** Named keys that are forwarded. Same list as the server's `ALLOWED_KEYS`. */
const NAMED_KEYS: ReadonlySet<string> = new Set([
  "Enter",
  "Tab",
  "Backspace",
  "Delete",
  "Escape",
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "Home",
  "End",
  "PageUp",
  "PageDown",
]);

export interface KeyLike {
  key: string;
  ctrlKey: boolean;
  altKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
  isComposing?: boolean;
}

/**
 * Shift+Escape leaves the remote screen. It is never forwarded, so a
 * keyboard user is not trapped in the canvas (WCAG 2.1.2).
 */
export function isReleaseKey(e: KeyLike): boolean {
  return e.key === "Escape" && e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey;
}

/**
 * The event to send for a key press, or null when the key is not
 * forwarded (browser shortcuts, function keys, anything with Alt or
 * Meta, and every Ctrl chord except select-all).
 */
export function keyToEvent(e: KeyLike): LiveInputEvent | null {
  if (e.isComposing || isReleaseKey(e)) return null;
  if (e.altKey || e.metaKey) return null;
  if (e.ctrlKey) {
    return !e.shiftKey && e.key.toLowerCase() === "a" ? { type: "key", key: "Control+A" } : null;
  }
  if (e.key === "Tab") return { type: "key", key: e.shiftKey ? "Shift+Tab" : "Tab" };
  if (e.key === " ") return { type: "text", text: " " };
  if (NAMED_KEYS.has(e.key)) return { type: "key", key: e.key };
  // A single printable character (Shift already applied by the browser).
  if ([...e.key].length === 1 && isPrintable(e.key)) return { type: "text", text: e.key };
  return null;
}

function isPrintable(text: string): boolean {
  // No C0 / C1 control characters. A newline is the Enter key.
  // eslint-disable-next-line no-control-regex
  return !/[\u0000-\u001f\u007f-\u009f]/.test(text);
}

/**
 * Pasted or composed text → `text` events. Control characters are
 * removed and the text is cut into chunks the server accepts.
 */
export function textToEvents(raw: string): LiveInputEvent[] {
  // eslint-disable-next-line no-control-regex
  const clean = raw.replace(/[\u0000-\u001f\u007f-\u009f]/g, "");
  const chars = [...clean];
  const events: LiveInputEvent[] = [];
  for (let i = 0; i < chars.length; i += MAX_TEXT_CHARS) {
    events.push({ type: "text", text: chars.slice(i, i + MAX_TEXT_CHARS).join("") });
  }
  return events;
}

export interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * A pointer position on the displayed canvas → a coordinate in the
 * runner's viewport. Null when the canvas has no size. Clamped: the
 * server rejects anything outside the viewport.
 */
export function toViewportPoint(
  clientX: number,
  clientY: number,
  box: Box,
  viewport: { width: number; height: number },
): { x: number; y: number } | null {
  if (box.width <= 0 || box.height <= 0) return null;
  const clamp = (value: number, max: number) => Math.min(Math.max(Math.round(value), 0), max - 1);
  return {
    x: clamp(((clientX - box.left) * viewport.width) / box.width, viewport.width),
    y: clamp(((clientY - box.top) * viewport.height) / box.height, viewport.height),
  };
}

export function mouseButton(button: number): "left" | "middle" | "right" | null {
  if (button === 0) return "left";
  if (button === 1) return "middle";
  if (button === 2) return "right";
  return null;
}

const clampDelta = (value: number) => Math.min(Math.max(Math.round(value), -2000), 2000);

export function wheelEvent(
  point: { x: number; y: number },
  deltaX: number,
  deltaY: number,
): LiveInputEvent {
  return { type: "wheel", ...point, delta_x: clampDelta(deltaX), delta_y: clampDelta(deltaY) };
}

type Send = (events: LiveInputEvent[]) => Promise<unknown>;

/**
 * Orders, coalesces and rate-limits outgoing input.
 *
 * - One request in flight at a time, so events arrive in the order
 *   they happened.
 * - Consecutive mouse moves collapse into the latest one.
 * - At most one request per `FLUSH_INTERVAL_MS`, at most
 *   `MAX_EVENTS_PER_BATCH` events per request.
 * - If a send fails, the queue is dropped (replaying stale input onto
 *   a page that has moved on is worse than losing it) and `onError`
 *   is told why.
 */
export class InputBatcher {
  private queue: LiveInputEvent[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private sending = false;
  private lastSendAt = 0;
  private stopped = false;

  constructor(
    private readonly send: Send,
    private readonly onError: (error: unknown) => void,
    private readonly now: () => number = () => Date.now(),
  ) {}

  push(event: LiveInputEvent): void {
    if (this.stopped) return;
    const last = this.queue[this.queue.length - 1];
    if (event.type === "mouse_move" && last?.type === "mouse_move") {
      this.queue[this.queue.length - 1] = event;
    } else {
      this.queue.push(event);
    }
    // Bound the backlog if sends stall: keep the newest input.
    if (this.queue.length > MAX_EVENTS_PER_BATCH * 4) {
      this.queue = this.queue.slice(-MAX_EVENTS_PER_BATCH * 2);
    }
    this.schedule();
  }

  /** Stop and forget anything not yet sent. */
  stop(): void {
    this.stopped = true;
    this.queue = [];
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
  }

  get pending(): number {
    return this.queue.length;
  }

  private schedule(): void {
    if (this.timer !== null || this.sending || this.queue.length === 0) return;
    const wait = Math.max(0, FLUSH_INTERVAL_MS - (this.now() - this.lastSendAt));
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.flush();
    }, wait);
  }

  private async flush(): Promise<void> {
    if (this.sending || this.stopped || this.queue.length === 0) return;
    const batch = this.queue.splice(0, MAX_EVENTS_PER_BATCH);
    this.sending = true;
    this.lastSendAt = this.now();
    try {
      await this.send(batch);
    } catch (error) {
      this.queue = [];
      this.onError(error);
    } finally {
      this.sending = false;
      this.schedule();
    }
  }
}
